'use client';

import React, { useEffect, useState } from 'react';
import { Modal, DatePicker, Image } from 'antd';
import { ButtonCommon } from '../Button/ButtonCommon';
import { Check } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { InputCommon } from '../Input/InputCommon';
import { formatPrice } from 'utils';
import { InventoryDocumentItem } from 'models';
interface SelectProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderItems: InventoryDocumentItem[];
  onConfirm: (
    selectedItems: InventoryDocumentItem[],
    returnDate?: Date,
    updatedPrices?: Record<string, number>,
  ) => void;
  originalReturnDate?: Date | string | null;
}

const SelectProductModal: React.FC<SelectProductModalProps> = ({
  isOpen,
  onClose,
  orderItems,
  onConfirm,
  originalReturnDate,
}) => {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [editablePrices, setEditablePrices] = useState<Record<string, number>>({});

  useEffect(() => {
    const initialPrices: Record<string, number> = {};
    orderItems.forEach((item) => {
      if (item?.id) {
        initialPrices[item.id] = item.costPrice;
      }
    });
    setEditablePrices(initialPrices);
  }, [orderItems]);
  const handlePriceChange = (itemId: string, newPrice: number) => {
    setEditablePrices((prev) => ({
      ...prev,
      [itemId]: newPrice,
    }));
  };
  const handleItemSelect = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleConfirm = () => {
    const selectedOrderItems = orderItems
      .filter((item) => selectedItems.has(item.id))
      .map((item) => ({
        ...item,
        costPrice: Number(editablePrices[item.id]) || (item.costPrice as number),
      }));

    const updatedPricesForSelected = Object.fromEntries(
      Array.from(selectedItems).map((itemId) => [
        itemId,
        editablePrices[itemId] || orderItems.find((item) => item.id === itemId)?.costPrice || 0,
      ]),
    );

    onConfirm(selectedOrderItems, returnDate || undefined, updatedPricesForSelected);
    handleClose();
  };
  useEffect(() => {
    if (selectedItems.size === orderItems.length) {
      setReturnDate(null);
    }
  }, [selectedItems.size, orderItems.length]);
  const handleClose = () => {
    setSelectedItems(new Set());
    setReturnDate(null);
    onClose();
  };
  const selectedCount = selectedItems.size;

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={682}
      centered
      className='select-product-modal modal-none-padding'
      maskClosable={false}
    >
      <div className='flex flex-col gap-4 bg-[var(--bgGreyColor)] rounded-lg pb-2'>
        {/* Header */}
        <div className='p-4 bg-white rounded-t-lg'>
          <h2 className='text-xl font-bold text-black mb-2'>CHỌN SẢN PHẨM NHẬP</h2>
          <p className='text-gray-500 text-sm'>Vui lòng chọn những sản phẩm nhập đợt này</p>
        </div>

        {/* Product List */}
        <div className='max-h-[80vh] overflow-y-auto space-y-3 bg-[var(--bgGreyColor)] p-4 pt-0 rounded-lg'>
          {orderItems.map((item) => {
            const isSelected = selectedItems.has(item.id);
            const isConfirmed = isSelected;
            const currentPrice = editablePrices[item.id];
            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 pd-card h-[114px] px-[8px] py-[12px] bg-[var(--textBlack4)] rounded-lg border transition-colors ${'border-[var(--textBlack2)] '}`}
              >
                {/* Product Image */}
                <Image className='!w-[90px] !h-[90px] rounded-lg object-cover bg-[var(--textBlack24)]' />

                {/* Product Info */}
                <div className='flex-1'>
                  <div className='flex items-center gap-2 mb-2'>
                    <span className='font-bold text-black'>{item.productVersion?.name}</span>
                  </div>

                  <div className='flex items-center gap-3 mb-2'>
                    {/* Quantity */}
                    <div className='px-2 py-1 bg-[var(--textBlack4)] rounded'>
                      <span className='text-sm text-[var(--textBlack56)] font-medium'>
                        {item.quantity} {item.productVersion?.baseUnit?.name}
                      </span>
                    </div>
                  </div>

                  <div className='flex items-center gap-2'>
                    <span className='text-sm text-gray-500'>Đơn giá:</span>
                    {!isConfirmed ? (
                      <InputCommon
                        type='money'
                        value={currentPrice}
                        twSize='sm'
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          const actualValue = rawValue === '' ? 0 : Number(rawValue);
                          handlePriceChange(item.id, actualValue);
                        }}
                        className=' text-sm font-medium !text-[var(--primaryColor500)] !w-[250px]'
                        suffix={item.productVersion?.currencyUnit?.name || 'VND'}
                      />
                    ) : (
                      <span className='text-sm font-medium text-blue-500 h-[32px] flex items-center'>
                        {formatPrice(currentPrice)} {item.productVersion?.currencyUnit?.name || 'VND'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Confirm Button */}
                <div className='flex items-end justify-end h-full'>
                  {isConfirmed ? (
                    <ButtonCommon
                      color='primary'
                      size='sm'
                      radius='rounded'
                      className='!bg-[var(--sematicSuccessColor500)] hover:!bg-[var(--tealColor700)]'
                      onClick={() => handleItemSelect(item.id, false)}
                      icon={<Check size={16} />}
                    >
                      Đã xác nhận
                    </ButtonCommon>
                  ) : (
                    <ButtonCommon
                      color='primary'
                      size='sm'
                      radius='rounded'
                      onClick={() => handleItemSelect(item.id, true)}
                      icon={<Check size={16} />}
                    >
                      Xác nhận
                    </ButtonCommon>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Return Summary */}
        <div className='  px-4 rounded-lg'>
          <div className='flex  gap-4 items-center'>
            <span className='font-medium min-w-fit text-gray-700'>Trả hàng:</span>
            <span className='font-bold min-w-fit'>
              {selectedCount}/{orderItems.length} sản phẩm
            </span>
            {
              <DatePicker
                placeholder='Thời gian trả phần còn lại'
                onChange={(date) => setReturnDate(date?.toDate() || null)}
                format='DD/MM/YYYY HH:mm'
                showTime={{
                  format: 'HH:mm',
                  minuteStep: 1,
                  hideDisabledOptions: true,
                }}
                className='flex-1 '
                disabledDate={(current) => {
                  const originalDay = dayjs(originalReturnDate).startOf('day');
                  return current && current.isBefore(originalDay);
                }}
                disabledTime={(current) => {
                  if (!current) return {};

                  const originalDateTime = dayjs(originalReturnDate);
                  const currentDate = current.startOf('day');

                  if (currentDate.isSame(originalDateTime.startOf('day'))) {
                    const originalHour = originalDateTime.hour();
                    const originalMinute = originalDateTime.minute();

                    return {
                      disabledHours: () => {
                        const hours = [];
                        for (let i = 0; i < originalHour; i++) {
                          hours.push(i);
                        }
                        return hours;
                      },
                      disabledMinutes: (selectedHour: number) => {
                        if (selectedHour === originalHour) {
                          const minutes = [];
                          for (let i = 0; i <= originalMinute; i++) {
                            minutes.push(i);
                          }
                          return minutes;
                        }
                        return [];
                      },
                    };
                  }

                  return {};
                }}
                value={returnDate ? dayjs(returnDate) : null}
                disabled={selectedCount === orderItems.length}
              />
            }
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex justify-center gap-4 pt-4'>
          <ButtonCommon
            color='none'
            size='md'
            radius='rounded'
            onClick={handleClose}
            className='!bg-transparent !text-black px-8'
          >
            Hủy
          </ButtonCommon>

          <ButtonCommon
            color='primary'
            size='md'
            radius='rounded'
            onClick={handleConfirm}
            isDisabled={selectedItems.size < orderItems.length && !returnDate}
            className='px-8'
          >
            {'Xác nhận nhập hàng'}
          </ButtonCommon>
        </div>
      </div>
    </Modal>
  );
};

export default SelectProductModal;
